/**
 * Legal Links
 *
 * Locale-aware footer links to the public legal pages (privacy notice,
 * terms of service). The legal routes are path-localized (`/cs/privacy`,
 * `/cs/terms` for Czech; bare paths for English — see routes/privacy.tsx)
 * while the rest of the app localizes via i18next state only, so the link
 * targets are derived from the active i18n language.
 */

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

export function LegalLinks() {
	const { t, i18n } = useTranslation()
	// `resolvedLanguage` is the language actually serving translations
	// (e.g. detector-reported `en-US` resolves to `en`, unsupported
	// languages resolve to the `cs` fallback) — it matches what the user
	// sees, so the legal page opens in the same language as the UI.
	const activeLanguage = i18n.resolvedLanguage ?? i18n.language
	const pathPrefix = activeLanguage?.startsWith('cs') ? '/cs' : ''

	return (
		<nav
			aria-label={t('footer.legal')}
			className="flex items-center gap-4 text-sm text-base-content/60"
		>
			<Link to={`${pathPrefix}/privacy`} className="hover:text-base-content hover:underline">
				{t('footer.privacy')}
			</Link>
			{/*
			 * Plain anchor on purpose: /downloads/ is served by the edge (Caddy
			 * file_server over /opt/polygraf/downloads — see
			 * docs/triton-operations.md "APK downloads drop"), not by this SPA, so
			 * a router <Link> would swallow the click and render the 404 route.
			 */}
			<a href="/downloads/" className="hover:text-base-content hover:underline">
				{t('footer.downloads')}
			</a>
			{/*
			 * The Terms of Service page (`/terms`) is authored but its content is a
			 * DRAFT pending MUNI legal review — do not advertise the route until it
			 * is approved. Restore this link (and it's already fully wired: route,
			 * i18n key `footer.terms`, bilingual page) once sign-off lands.
			 */}
		</nav>
	)
}
